/**
 * Everything the external-suite config page needs about the property behind it.
 *
 * @module features/test-suites/useExternalSuiteConfigData
 * @description One hook so the page has a single loading state instead of six.
 * It reuses the existing fetchers and — importantly — the existing query KEYS
 * (`['property', id]`, `['property-environments', id]`, `['property-credentials',
 * id]`, `['credential-templates', id]`, `['property-github-repos', id]`), so
 * this page shares a cache with the property tabs rather than opening a second,
 * independently-stale copy of the same data.
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { fetchOrgPropertyById } from '../../hooks/useOrgProperty';
import { usePropertyCredentialSummaries } from '../../hooks/usePropertyMetadata';
import { useCredentialTemplates } from '../../hooks/useCredentialTemplates';
import { usePropertyGitHubRepos } from '../releases/useReleases';
import { fetchPropertyEnvironments } from './fetchPropertyEnvironments';
import type { EnvVarDefinition } from '../../ui/components/property-tabs/property-fields';

/** The property fields the mapping editor reads. */
type PropertyWithEnvVars = {
  id: string;
  name: string;
  envVarDefinitions?: EnvVarDefinition[] | null;
};

export function useExternalSuiteConfigData(propertyId: string | null | undefined) {
  const enabled = Boolean(propertyId);

  const property = useQuery({
    queryKey: ['property', propertyId],
    enabled,
    queryFn: () => fetchOrgPropertyById<PropertyWithEnvVars>(propertyId as string),
  });

  const environments = useQuery({
    queryKey: ['property-environments', propertyId],
    enabled,
    queryFn: () => fetchPropertyEnvironments(propertyId as string),
  });

  const credentials = usePropertyCredentialSummaries(propertyId ?? undefined);
  const personas = useCredentialTemplates(propertyId ?? undefined);
  const repos = usePropertyGitHubRepos(propertyId ?? undefined);

  /**
   * Is GitHub connected at ALL? Distinguished from "connected but nothing
   * linked to this property" because the two have completely different fixes —
   * install the app vs. link a repo on the Code tab — and telling someone to
   * link a repo they cannot see is worse than saying nothing.
   */
  const githubStatus = useQuery({
    queryKey: ['github-status'],
    queryFn: async (): Promise<{ ok: boolean }> => {
      const res = await fetch(`${API_URL}/integrations/github/status`, { credentials: 'include' });
      return res.json();
    },
  });

  return {
    property,
    environments,
    credentials,
    personas,
    repos,
    githubStatus,
    isLoading:
      enabled &&
      (property.isLoading ||
        environments.isLoading ||
        credentials.isLoading ||
        personas.isLoading ||
        repos.isLoading),
  };
}
